import { editor as MonacoEditor, Range } from 'monaco-editor';
import type { FrameworkAction, FrameworkArgument, FrameworkClass } from '../../../framework/framework.d';
import { getInputArgNameForAxisAction } from '../../../framework/framework-axis-actions';
import { FrameworkClasses } from '../../../framework/frameworkClasses';
import type { InputBinding, InputBindingAction, InputBindingDevice, SingleActionInputBinding } from '../../../utils/inputBindings';
import {
  collapseDuplicateToggleBindings,
  dedupeInputBindingsBySignature,
  getSupportedInputBindingsForButtonEditor,
  getUnsupportedInputBindingActionExpression,
  INPUT_BINDING_DEVICE,
  INPUT_BINDING_KIND,
  inputBindingSignature,
  isUnsupportedInputBinding,
  isSupportedInputBindingForButtonEditor,
  inputBindingTriggerMethod,
} from '../../../utils/inputBindings';
import {
  clampInsertLineBeforeRobotStart,
  escapeRegex,
  findInputMappingsRegionBlockBounds,
  getRemoveEmptyInputMappingsRegionEdit,
  findRobotStartLine,
  getInsertLineBeforeRobotStart,
  getLastMappingScanLineBeforeRobotStart,
  removeEmptyInputMappingsRegionIfNeeded,
} from '../../../utils/monacoRegionUtils';
import { formatPythonActionArgument, formatPythonTypedScalar } from '../../../utils/pythonActionFormatting';
import { formatGeneratedPythonCall } from '../../../utils/pythonFormattingUtils';
import {
  bumpInsertLineForTopLevelVarDeclarationAnchors,
  collectTopLevelVarAssignments,
  findNextTopLevelVarAssignmentLineIndex,
  hasTopLevelVarAssignmentAfterLine,
  findConstructorCallEndLineIndex,
  compactSpanContainsArgToken,
  extractTrailingPythonLineComment,
  findTopLevelKeywordAssignmentIndex,
  findTopLevelVarDeclarationEndLine,
  findVarAssignmentLineIndex,
  getTextModelLines,
  advancePythonDelimiterNestingDepth,
  leadingWhitespaceLength,
  normalizePythonCallCompact,
  splitCodeLines,
  splitTopLevelByComma,
  findMatchingDelimiterIndex,
  type TopLevelVarDeclarationAnchor,
} from '../../../utils/pythonSourceScanUtils';
import { getFrameworkActionTargetAllowedClassNames, parseFrameworkClassArgTypeNames } from '../../../utils/frameworkClassNameSets';
import { INPUT_MAPPINGS_REGION_START, REGION_END } from '../../../utils/programRegionMarkers';
import { getMainProgramEditorProxy } from '../../CodeEditor/utils/mainProgramEditor';
import {
  shouldPreserveRawDriveUnitPortArgument,
  shouldPreserveRawFrameworkBoolArgument,
  shouldPreserveRawFrameworkElectronicArgument,
  shouldPreserveRawFrameworkEnumArgument,
  shouldPreserveRawFrameworkNumericArgument,
  shouldPreserveRawFrameworkStringArgument,
} from '../../../utils/frameworkArgTypes';
import { isSequenceTupleArg, isTupleArg, readSequenceTupleMemberValue, readSequenceTupleRows, readTupleMemberValue } from '../../../utils/tupleArg';
import {
  extractInputBindingIrFromSourceSync,
  findAxisBindingLineRangesForGetterSync,
  findButtonTriggerBindingLineRangesSync,
  findLastInputBindingEndLineForReceiverSync,
  findUnsupportedBindingLineRangesSync,
  findVariableAxisBindingLineRangesSync,
  hasRemainingInputBindingsForReceiverSync,
  isPythonTreeSitterReady,
  resolveUnsupportedBindingDeleteEditSync,
  type TreeSitterInputBindingIr,
} from '../../../utils/pythonTreeSitterInputBindings';

const { CONTROLLER, DRIVEBASE } = INPUT_BINDING_DEVICE;

/** Shared fail-closed reasons when Tree-sitter is unavailable for binding mutations. */
export const TREE_SITTER_NOT_READY_REASON = {
  delete: 'Tree-sitter is not ready for input-binding delete.',
  insert: 'Tree-sitter is not ready for input-binding insert.',
  replace: 'Tree-sitter is not ready for input-binding replace.',
} as const;

/** Soft-no-op save/delete: source no longer matches the mapping the UI thought it was editing. */
export const STALE_MAPPING_IN_EDITOR_REASON = {
  update: 'Could not update this mapping; the code in the editor may have changed. Close the dialog and try again.',
  remove: 'Could not remove this mapping; the code in the editor may have changed.',
} as const;

/** Source holds one call per button + trigger + action, so a second row cannot be added. */
export const DUPLICATE_MAPPING_IDENTITY_REASON =
  'Could not add this mapping; this button already runs this action on the same trigger. Edit the existing mapping instead.';

const AXIS_BUTTON_TO_GETTER: Record<string, string> = {
  r1: 'get_r1_axis',
  l1: 'get_l1_axis',
  r2: 'get_r2_axis',
  l2: 'get_l2_axis',
};

export type CodeMutationResult =
  | {
      ok: true;
      changed: boolean;
    }
  | {
      ok: false;
      reason: string;
    };

function treeSitterNotReadyResult(op: keyof typeof TREE_SITTER_NOT_READY_REASON): Extract<CodeMutationResult, { ok: false }> {
  return { ok: false, reason: TREE_SITTER_NOT_READY_REASON[op] };
}

const UNSAFE_INPUT_PANEL_MAPPING_RELOCATION_REASON =
  'Cannot safely relocate mapping because required declarations fall after the receiver is reassigned.';
const UNSAFE_INPUT_PANEL_MAPPING_SCOPE_RELOCATION_REASON = 'Cannot safely relocate mapping because moving it would change its original Python scope.';

interface BindingSourceRange {
  startLine: number;
  endLine: number;
}

export type SaveButtonBindingsResult =
  | {
      ok: true;
      changed: boolean;
      nextBindingList: InputBinding[];
    }
  | {
      ok: false;
      reason: string;
    };

function getStoredReceiverVarName(binding: InputBinding): string | null {
  const receiverVarName = String(binding.receiverVarName || '').trim();
  return receiverVarName || null;
}

function resolveBindingReceiverVarName(model: MonacoEditor.ITextModel, binding: InputBinding): string {
  const storedReceiverVarName = getStoredReceiverVarName(binding);
  if (storedReceiverVarName) return storedReceiverVarName;
  return binding.device === DRIVEBASE ? resolveDrivebasePanelVarName(model) : resolveControllerVarName(model);
}

function withBindingReceiverVarName<T extends InputBinding>(binding: T, receiverVarName: string | null | undefined): T {
  const normalizedReceiverVarName = String(receiverVarName || '').trim();
  if (!normalizedReceiverVarName || normalizedReceiverVarName === String(binding.receiverVarName || '').trim()) return binding;
  return {
    ...binding,
    receiverVarName: normalizedReceiverVarName,
  };
}

export function inferSingleReceiverVarName<T extends { receiverVarName?: string }>(items: ReadonlyArray<T>): string | null {
  const receiverVarNames = Array.from(
    new Set(
      items
        .map((item) => {
          const receiverVarName = String(item.receiverVarName || '').trim();
          return receiverVarName || null;
        })
        .filter((receiverVarName): receiverVarName is string => !!receiverVarName)
    )
  );
  return receiverVarNames.length === 1 ? receiverVarNames[0] : null;
}

export function canonicalizeButtonBindingsForSave(
  editor: MonacoEditor.IStandaloneCodeEditor | undefined,
  previousBindings: ReadonlyArray<InputBinding>,
  nextBindings: ReadonlyArray<InputBinding>
): InputBinding[] {
  const inferredReceiverVarName = inferSingleReceiverVarName(previousBindings);
  const targetEditor = editor ? getMainProgramEditorProxy(editor) ?? editor : undefined;
  const model = targetEditor?.getModel();

  return nextBindings.map((binding) => {
    const bindingWithInferredReceiver =
      !getStoredReceiverVarName(binding) && inferredReceiverVarName ? withBindingReceiverVarName(binding, inferredReceiverVarName) : binding;
    if (!model) return bindingWithInferredReceiver;
    return withBindingReceiverVarName(bindingWithInferredReceiver, resolveBindingReceiverVarName(model, bindingWithInferredReceiver));
  });
}

function withBindingSourceLineNumber<T extends InputBinding>(binding: T, sourceLineNumber: number | undefined): T {
  if (binding.sourceLineNumber === sourceLineNumber) return binding;
  const next = { ...binding };
  if (sourceLineNumber === undefined) delete next.sourceLineNumber;
  else next.sourceLineNumber = sourceLineNumber;
  return next;
}

/**
 * Re-anchor bindings to the spans they actually occupy in the current source.
 *
 * Insert, delete, relocate, and identity-collapse shift or move mappings, so a
 * pre-mutation `sourceLineNumber` becomes a stale hard anchor that makes the next anchored
 * mutation fail closed (`changed: false` while the call is still in Main.py). Rows with no
 * matching span drop the anchor entirely rather than keep a wrong line, so follow-up edits
 * fall back to unanchored selection.
 *
 * Extracts Tree-sitter IR once and matches every row against that snapshot.
 */
export function reanchorInputBindingsToSource(editor: MonacoEditor.IStandaloneCodeEditor | undefined, bindings: InputBinding[]): InputBinding[] {
  if (!editor) return bindings;
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  const model = typeof targetEditor.getModel === 'function' ? targetEditor.getModel() : null;
  if (!model || !isPythonTreeSitterReady()) return bindings;

  const code = getCodeTextFromModel(model);
  const ir = extractInputBindingIrFromSourceSync(code);
  if (!ir) return bindings;

  const claimedStartLines = new Set<number>();
  return bindings.map((binding) => {
    const unanchored = withBindingSourceLineNumber(binding, undefined);
    const ranges = isUnsupportedInputBinding(unanchored)
      ? findUnsupportedRawSpanRanges(model, unanchored, ir)
      : findSemanticDirectBindingRanges(model, resolveBindingReceiverVarName(model, unanchored), unanchored, ir);
    const range = ranges.find((candidate) => !claimedStartLines.has(candidate.startLine));
    if (!range) return unanchored;
    claimedStartLines.add(range.startLine);
    return withBindingSourceLineNumber(binding, range.startLine);
  });
}

/**
 * A save can consume the call an unsupported row was hydrated from: a new dialog row whose
 * button + trigger + action matches a hand-authored nested call rewrites that call in place
 * instead of inserting a duplicate. The unsupported row then describes a span that no longer
 * exists, leaving two Redux rows against one call.
 *
 * Only rows for the saved button are reconciled, and only when their raw span is absent from the
 * mutated source, so unrelated rows and unmatched spans elsewhere still fail closed.
 *
 * Spans are matched unanchored, because the same save can also delete lines above a surviving
 * call: an anchored lookup would miss the shifted span and read a merely moved call as consumed.
 * Each row claims a distinct span so duplicate raw calls keep one row per surviving span.
 */
function dropUnsupportedRowsWithoutSourceSpan(
  editor: MonacoEditor.IStandaloneCodeEditor | undefined,
  bindings: ReadonlyArray<InputBinding>,
  device: InputBindingDevice,
  button: string
): InputBinding[] {
  if (!editor) return [...bindings];
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  const model = typeof targetEditor.getModel === 'function' ? targetEditor.getModel() : null;
  if (!model || !isPythonTreeSitterReady()) return [...bindings];

  const ir = extractInputBindingIrFromSourceSync(getCodeTextFromModel(model));
  if (!ir) return [...bindings];

  const claimedStartLines = new Set<number>();
  return bindings.filter((binding) => {
    if (binding.device !== device || String(binding.button || '') !== button) return true;
    if (!isUnsupportedInputBinding(binding)) return true;
    const ranges = findUnsupportedRawSpanRanges(model, withBindingSourceLineNumber(binding, undefined), ir);
    const range = ranges.find((candidate) => !claimedStartLines.has(candidate.startLine));
    if (!range) return false;
    claimedStartLines.add(range.startLine);
    return true;
  });
}

export function saveButtonBindings(
  editor: MonacoEditor.IStandaloneCodeEditor | undefined,
  bindingList: ReadonlyArray<InputBinding>,
  device: InputBindingDevice,
  button: string,
  nextBindings: ReadonlyArray<InputBinding>
): SaveButtonBindingsResult {
  const previousEditableBindings = getSupportedInputBindingsForButtonEditor([...bindingList], device, button);
  // Last toggle for a button wins: duplicate on_toggle rows are invalid at runtime, and
  // Monaco collapses cross-matching swapped siblings to one call — Redux must match that.
  const dedupedNextBindings = dedupeInputBindingsBySignature(
    collapseDuplicateToggleBindings(canonicalizeButtonBindingsForSave(editor, previousEditableBindings, nextBindings))
  );

  let changed =
    previousEditableBindings.length !== dedupedNextBindings.length ||
    previousEditableBindings.some((binding, index) => inputBindingSignature(binding) !== inputBindingSignature(dedupedNextBindings[index]));

  let savedNextBindings = dedupedNextBindings;

  if (editor) {
    const result = replaceButtonInputBindingsInCode(editor, previousEditableBindings, dedupedNextBindings);
    if (!result.ok) return result;
    changed = result.changed;
    // Replace collapses top-level same-identity peers in Main.py; keep Redux on that single winner.
    // Canonicalize previous the same way replace does so pairing keys (receiver) match.
    const canonicalPreviousBindings = canonicalizeButtonBindingsForSave(editor, previousEditableBindings, previousEditableBindings);
    savedNextBindings = dropSameIdentityPeersCollapsedByReplace(canonicalPreviousBindings, dedupedNextBindings);
    if (savedNextBindings.length !== dedupedNextBindings.length) {
      changed = true;
    }
  }

  const carriedOverBindings = bindingList.filter((binding) => !isSupportedInputBindingForButtonEditor(binding, device, button));
  const retainedCarriedOverBindings = dropUnsupportedRowsWithoutSourceSpan(editor, carriedOverBindings, device, button);
  if (retainedCarriedOverBindings.length !== carriedOverBindings.length) {
    changed = true;
  }

  const nextBindingList = [...retainedCarriedOverBindings, ...savedNextBindings];

  return {
    ok: true,
    changed,
    // Saving can relocate the edited call and shift every mapping below it, so hand Redux
    // spans from the mutated source instead of the pre-save anchors.
    nextBindingList: reanchorInputBindingsToSource(editor, nextBindingList),
  };
}

function getAxisGetterForButton(button: string): string | null {
  return AXIS_BUTTON_TO_GETTER[String(button || '').toLowerCase()] ?? null;
}

function shouldPreserveRawFrameworkArgument(arg: FrameworkArgument, value: unknown, actionName?: string): boolean {
  return (
    shouldPreserveRawDriveUnitPortArgument(arg, value) ||
    shouldPreserveRawFrameworkNumericArgument(arg, value) ||
    shouldPreserveRawFrameworkEnumArgument(arg, value) ||
    shouldPreserveRawFrameworkBoolArgument(arg, value, arg.rawSourceExpression) ||
    shouldPreserveRawFrameworkElectronicArgument(arg, value, arg.rawSourceExpression) ||
    shouldPreserveRawFrameworkStringArgument(arg, value, actionName, arg.rawSourceExpression)
  );
}

function formatInputBindingTupleMembers(
  members: FrameworkArgument[],
  getMemberValue: (member: FrameworkArgument, index: number) => unknown,
  actionName: string
): string {
  const formattedMembers = members.map((member, index) => {
    const memberValue = getMemberValue(member, index) ?? member.defaultValue;
    if (shouldPreserveRawFrameworkArgument(member, memberValue, actionName)) {
      return String(member.rawSourceExpression || memberValue).trim();
    }
    return formatPythonTypedScalar(memberValue, { name: member.name, type: member.type }, actionName);
  });
  return `(${formattedMembers.join(', ')})`;
}

function formatInputBindingTupleValue(arg: FrameworkArgument, actionName: string): string {
  return formatInputBindingTupleMembers(arg.members || [], (_member, index) => readTupleMemberValue(arg, index, _member.name), actionName);
}

function formatInputBindingSequenceTupleValue(arg: FrameworkArgument, actionName: string): string {
  const members = arg.members || [];
  const rows = readSequenceTupleRows(arg).map((row) =>
    formatInputBindingTupleMembers(members, (member, index) => readSequenceTupleMemberValue(row, index, member.name), actionName)
  );
  return `[${rows.join(', ')}]`;
}

function formatInputBindingActionArgument(arg: FrameworkArgument, actionName?: string): string {
  if (isSequenceTupleArg(arg)) {
    if (Array.isArray(arg.value)) {
      return `${arg.name}=${formatInputBindingSequenceTupleValue(arg, String(actionName || ''))}`;
    }
    // Preserved unsupported expression (e.g. `get_lines()`) — emit verbatim rather than quoting it.
    const rawValue = String(arg.value ?? '').trim();
    if (rawValue) return `${arg.name}=${rawValue}`;
    return `${arg.name}=[]`;
  }
  if (isTupleArg(arg)) {
    if (arg.rawSourceExpression) return `${arg.name}=${String(arg.rawSourceExpression).trim()}`;
    return `${arg.name}=${formatInputBindingTupleValue(arg, String(actionName || ''))}`;
  }
  if (arg.rawSourceExpression) {
    return `${arg.name}=${String(arg.rawSourceExpression).trim()}`;
  }
  if (shouldPreserveRawFrameworkArgument(arg, arg.value, actionName)) {
    return `${arg.name}=${String(arg.rawSourceExpression || arg.value).trim()}`;
  }
  return formatPythonActionArgument(arg, actionName);
}

function buildActionQualified(bindingAction: InputBindingAction): { isCustomAction: boolean; actionQualified: string } {
  const isCustomAction = String(bindingAction.parentName || '').toLowerCase() === 'custom' && !bindingAction.mechanismName;
  const hasActionName = String(bindingAction.action?.name || '').trim().length > 0;
  const actionParts = [bindingAction.parentName, bindingAction.mechanismName, bindingAction.action.name].filter(Boolean);
  const actionQualified = hasActionName
    ? isCustomAction
      ? String(bindingAction.action.name || '')
      : actionParts.filter((part, index) => part !== actionParts[index - 1]).join('.')
    : '';
  return { isCustomAction, actionQualified };
}

function buildActionCall(bindingAction: InputBindingAction): string | null {
  const unsupportedExpression = getUnsupportedInputBindingActionExpression(bindingAction);
  if (unsupportedExpression) return unsupportedExpression;

  const { isCustomAction, actionQualified } = buildActionQualified(bindingAction);
  if (!actionQualified) return null;
  if (isCustomAction) return actionQualified;
  const pythonArgs = (bindingAction.action.arguments || [])
    .map((arg: FrameworkArgument) => formatInputBindingActionArgument(arg, bindingAction.action.name))
    .join(', ');
  return `${actionQualified}(${pythonArgs})`;
}

function buildAxisInputControlLine(controllerVarName: string, binding: SingleActionInputBinding): string | null {
  const getter = getAxisGetterForButton(binding.button);
  if (!getter) return null;
  const actionCallTarget = buildActionQualified(binding.action).actionQualified;
  if (!actionCallTarget) return null;
  const actionName = binding.action.action.name;
  const inputArgName = getInputArgNameForAxisAction(actionName) ?? 'variable_input';
  const args = (binding.action.action.arguments || [])
    .filter((arg: FrameworkArgument) => String(arg?.name || '') !== inputArgName)
    .map((arg: FrameworkArgument) => formatInputBindingActionArgument(arg, actionName))
    .join(', ');
  return `${actionCallTarget}(${inputArgName}=${controllerVarName}.${getter}${args ? `, ${args}` : ''})`;
}

function bindingMethod(binding: SingleActionInputBinding): 'on_pressed' | 'on_released' | null {
  if (binding.kind === INPUT_BINDING_KIND.PRESSED) return 'on_pressed';
  if (binding.kind === INPUT_BINDING_KIND.RELEASED) return 'on_released';
  return null;
}

function buildDirectBindingLine(varName: string, binding: InputBinding): string | null {
  if (isUnsupportedInputBinding(binding)) return null;
  if (binding.kind === INPUT_BINDING_KIND.TOGGLE) {
    const onActionCall = buildActionCall(binding.onAction);
    const offActionCall = buildActionCall(binding.offAction);
    if (!onActionCall || !offActionCall) return null;
    return `${varName}.${binding.button}().on_toggle(${onActionCall}, ${offActionCall})`;
  }
  if (binding.kind === INPUT_BINDING_KIND.VARIABLE) return buildAxisInputControlLine(varName, binding);
  const method = bindingMethod(binding);
  const actionCall = buildActionCall(binding.action);
  if (!method || !actionCall) return null;
  return `${varName}.${binding.button}().${method}(${actionCall})`;
}

function buildFormattedDirectBindingLine(varName: string, binding: InputBinding): string | null {
  const line = buildDirectBindingLine(varName, binding);
  return line ? formatGeneratedPythonCall(line) : null;
}

function getActionTargetDeclarationName(action: InputBindingAction): string | null {
  const parentName = String(action.parentName || '').trim();
  if (!parentName || parentName.toLowerCase() === 'custom' || parentName === 'UtilityActions') return null;
  const mechanismName = String(action.mechanismName || '').trim();
  if (mechanismName) return mechanismName;
  return parentName;
}

type ActionTargetDeclarationAnchor = TopLevelVarDeclarationAnchor;

function getActionTargetDeclarationAnchor(action: InputBindingAction): ActionTargetDeclarationAnchor | null {
  const variableName = getActionTargetDeclarationName(action);
  if (!variableName) return null;
  return {
    variableName,
    allowedClassNames: getFrameworkActionTargetAllowedClassNames(action.mechanismClass?.name ?? action.parentClass?.name),
  };
}

function getElectronicArgumentDeclarationAnchor(arg: FrameworkArgument): ActionTargetDeclarationAnchor | null {
  const allowedClassNames = new Set(parseFrameworkClassArgTypeNames(arg.type));
  if (allowedClassNames.size === 0) return null;
  const variableName = String(arg.rawSourceExpression ?? arg.value ?? '').trim();
  if (!/^[A-Za-z_][\w]*$/.test(variableName)) return null;
  return {
    variableName,
    allowedClassNames,
    preferLastEffectiveDeclaration: true,
  };
}

function collectActionTargetDeclarationAnchors(binding: InputBinding): ActionTargetDeclarationAnchor[] {
  if (isUnsupportedInputBinding(binding)) return [];
  const anchors = new Map<string, ActionTargetDeclarationAnchor>();
  const addActionTarget = (action: InputBindingAction) => {
    const anchor = getActionTargetDeclarationAnchor(action);
    if (!anchor) return;
    const existing = anchors.get(anchor.variableName);
    if (!existing?.allowedClassNames || anchor.allowedClassNames) {
      anchors.set(anchor.variableName, anchor);
    }
    for (const arg of action.action.arguments || []) {
      const electronicAnchor = getElectronicArgumentDeclarationAnchor(arg as FrameworkArgument);
      if (!electronicAnchor) continue;
      const existingElectronic = anchors.get(electronicAnchor.variableName);
      if (!existingElectronic?.allowedClassNames || electronicAnchor.allowedClassNames) {
        anchors.set(electronicAnchor.variableName, electronicAnchor);
      }
    }
  };
  if (binding.kind === INPUT_BINDING_KIND.TOGGLE) {
    addActionTarget(binding.onAction);
    addActionTarget(binding.offAction);
    return Array.from(anchors.values());
  }
  addActionTarget(binding.action);
  return Array.from(anchors.values());
}

function getInsertLineAfterInputPanelDeclaration(
  model: MonacoEditor.ITextModel,
  className: 'ActionController' | 'ActionDriveBasePanel',
  defaultVarName: string
): number | null {
  const variableName = resolveInputPanelVarName(model, className, defaultVarName);
  const declEndLine = findTopLevelVarDeclarationEndLine(model, variableName, {
    allowedClassNames: new Set([className]),
  });
  return declEndLine !== null ? declEndLine + 1 : null;
}

function hasTopLevelInputPanelDeclaration(model: MonacoEditor.ITextModel, className: 'ActionController' | 'ActionDriveBasePanel'): boolean {
  const lines = getTextModelLines(model);
  return collectTopLevelVarAssignments(lines, { allowedClassNames: new Set([className]) }).length > 0;
}

function resolveInputPanelVarName(
  model: MonacoEditor.ITextModel,
  className: 'ActionController' | 'ActionDriveBasePanel',
  defaultName: string
): string {
  const lines = getTextModelLines(model);
  const assignments = collectTopLevelVarAssignments(lines, {
    allowedClassNames: new Set([className]),
  });
  return assignments.at(-1)?.variableName ?? defaultName;
}

export function resolveControllerVarName(model: MonacoEditor.ITextModel): string {
  return resolveInputPanelVarName(model, 'ActionController', 'controller');
}

export function resolveDrivebasePanelVarName(model: MonacoEditor.ITextModel): string {
  return resolveInputPanelVarName(model, 'ActionDriveBasePanel', 'drivebase_panel');
}

function inputPanelClassName(device: InputBinding['device']): 'ActionController' | 'ActionDriveBasePanel' {
  return device === DRIVEBASE ? 'ActionDriveBasePanel' : 'ActionController';
}

function getInputPanelDeclarationEndLine(
  model: MonacoEditor.ITextModel,
  varName: string,
  device: InputBinding['device'],
  beforeLineNumber?: number
): number | null {
  const lines = getTextModelLines(model);
  const declLineIndex = findInputPanelDeclarationLineIndex(lines, varName, inputPanelClassName(device), beforeLineNumber);
  return declLineIndex === null ? null : findConstructorCallEndLineIndex(lines, declLineIndex) + 1;
}

function getInsertLineForInputPanelMappingsBlock(model: MonacoEditor.ITextModel, varName: string, device: InputBinding['device']): number {
  const insertBeforeStart = getInsertLineBeforeRobotStart(model);
  if (insertBeforeStart !== null) return insertBeforeStart;
  const declEndLine = getInputPanelDeclarationEndLine(model, varName, device);
  if (declEndLine !== null) return declEndLine;
  return model.getLineCount() + 1;
}

/** 1-based line for the next input-panel mapping, always after its declaration (even outside the managed region). */
type InputPanelBindingInsertLineResult =
  | {
      ok: true;
      insertLine: number;
    }
  | {
      ok: false;
      reason: string;
      fallbackInsertLine: number;
    };

function resolveInsertLineForInputPanelBinding(
  model: MonacoEditor.ITextModel,
  varName: string,
  device: InputBinding['device'],
  binding?: InputBinding,
  ownerLineNumber?: number
): InputPanelBindingInsertLineResult {
  const declEndLine = getInputPanelDeclarationEndLine(model, varName, device, ownerLineNumber);
  if (declEndLine === null) {
    const insertLine = getInsertLineForInputPanelMappingsBlock(model, varName, device);
    return { ok: true, insertLine };
  }

  const lines = getTextModelLines(model);
  const declLineIndex = findInputPanelDeclarationLineIndex(lines, varName, inputPanelClassName(device), ownerLineNumber);
  const nextAssignmentLineIndex = declLineIndex === null ? null : findNextTopLevelVarAssignmentLineIndex(lines, varName, declLineIndex);
  const axisControllerVarName = device === CONTROLLER ? varName : resolveControllerVarName(model);
  const robotStartLine = findRobotStartLine(model);
  // Highest priority: keep mappings with the panel declaration. If the declaration is at/after
  // robot.start(), do not pull inserts back above start.
  const declarationIsAtOrAfterStart = robotStartLine !== null && declEndLine >= robotStartLine;
  const lastScanBeforeStart = getLastMappingScanLineBeforeRobotStart(model);
  const maxScanLine =
    nextAssignmentLineIndex === null
      ? declarationIsAtOrAfterStart
        ? model.getLineCount()
        : lastScanBeforeStart
      : Math.min(declarationIsAtOrAfterStart ? model.getLineCount() : lastScanBeforeStart, nextAssignmentLineIndex);
  // Tree-sitter only — no regex scan for the last mapping in the insert window.
  const lastBindingEndLine = findLastInputBindingEndLineForReceiverSync(model.getValue(), {
    receiverVarName: varName,
    axisControllerVarName,
    afterLine: declEndLine,
    maxLine: maxScanLine,
  });
  const lastMappingLine = lastBindingEndLine ?? declEndLine;
  const nextAssignmentInsertLine = nextAssignmentLineIndex === null ? null : nextAssignmentLineIndex + 1;
  const candidateInsertLine = lastMappingLine + 1;
  const normalInsertLine = declarationIsAtOrAfterStart ? candidateInsertLine : clampInsertLineBeforeRobotStart(model, candidateInsertLine);
  const boundedNormalInsertLine = nextAssignmentInsertLine === null ? normalInsertLine : Math.min(normalInsertLine, nextAssignmentInsertLine);
  if (!binding) return { ok: true, insertLine: boundedNormalInsertLine };
  const anchoredInsertLine = bumpInsertLineForTopLevelVarDeclarationAnchors(
    model,
    boundedNormalInsertLine,
    collectActionTargetDeclarationAnchors(binding)
  );
  if (nextAssignmentInsertLine !== null && anchoredInsertLine > nextAssignmentInsertLine) {
    return {
      ok: false,
      reason: UNSAFE_INPUT_PANEL_MAPPING_RELOCATION_REASON,
      fallbackInsertLine: nextAssignmentInsertLine,
    };
  }
  return { ok: true, insertLine: anchoredInsertLine };
}

function getInsertLineForInputPanelBinding(
  model: MonacoEditor.ITextModel,
  varName: string,
  device: InputBinding['device'],
  binding?: InputBinding,
  ownerLineNumber?: number
): number {
  const result = resolveInsertLineForInputPanelBinding(model, varName, device, binding, ownerLineNumber);
  return result.ok ? result.insertLine : result.fallbackInsertLine;
}

function getMaxDeclaredActionTargetInsertLine(model: MonacoEditor.ITextModel, binding: InputBinding): number {
  return bumpInsertLineForTopLevelVarDeclarationAnchors(model, 0, collectActionTargetDeclarationAnchors(binding));
}

function bindingLineNeedsRelocation(model: MonacoEditor.ITextModel, bindingLine: number, binding: InputBinding): boolean {
  const actionTargetInsertLine = getMaxDeclaredActionTargetInsertLine(model, binding);
  if (actionTargetInsertLine > 0 && actionTargetInsertLine > bindingLine) return true;

  const startLine = findRobotStartLine(model);
  if (startLine === null || bindingLine < startLine) return false;

  const varName = binding.device === DRIVEBASE ? resolveDrivebasePanelVarName(model) : resolveControllerVarName(model);
  return getInsertLineForInputPanelBinding(model, varName, binding.device) < startLine;
}

function unsafeRelocationReasonShouldAbortButtonSave(reason: string): boolean {
  return reason === UNSAFE_INPUT_PANEL_MAPPING_RELOCATION_REASON || reason === UNSAFE_INPUT_PANEL_MAPPING_SCOPE_RELOCATION_REASON;
}

function rejectRelocationThatWouldChangeScope(model: MonacoEditor.ITextModel, sourceLineNumber: number): CodeMutationResult | null {
  return leadingWhitespaceLength(model.getLineContent(sourceLineNumber)) > 0
    ? { ok: false, reason: UNSAFE_INPUT_PANEL_MAPPING_SCOPE_RELOCATION_REASON }
    : null;
}

/**
 * Match an unsupported binding's stored raw span via Tree-sitter discovery
 * (button triggers and axis/variable controls). Fail closed when Tree-sitter is not
 * ready or no span matches the trimmed rawCode.
 */
function findUnsupportedRawSpanRanges(
  model: MonacoEditor.ITextModel,
  binding: InputBinding,
  ir?: TreeSitterInputBindingIr | null
): BindingSourceRange[] {
  if (!isUnsupportedInputBinding(binding)) return [];
  const raw = String(binding.rawCode || '').trim();
  if (!raw) return [];

  const varName = resolveBindingReceiverVarName(model, binding);
  return findUnsupportedBindingLineRangesSync(
    model.getValue(),
    varName,
    {
      rawCode: raw,
      buttonName: binding.button,
      triggerToken: inputBindingTriggerMethod(binding),
      sourceLineNumber: binding.sourceLineNumber,
    },
    ir
  ).map((range) => ({ startLine: range.startLine, endLine: range.endLine }));
}

function matchUnsupportedRawSpan(model: MonacoEditor.ITextModel, binding: InputBinding): BindingSourceRange | null {
  return findUnsupportedRawSpanRanges(model, binding)[0] ?? null;
}

function applyLeadingIndentFromLine(sourceLine: string, unindentedLine: string): string {
  const match = /^(\s*)/.exec(sourceLine);
  const indent = match?.[1] ?? '';
  return indent
    ? unindentedLine
        .split('\n')
        .map((line) => `${indent}${line}`)
        .join('\n')
    : unindentedLine;
}

/** Collect unique trailing `#` comments from outer call-arg lines (not nested tuple/list bodies). */
function collectInlineCommentsFromBindingSpan(spanText: string): string[] {
  const comments: string[] = [];
  const seen = new Set<string>();
  let depth = 0;
  for (const line of splitCodeLines(spanText)) {
    const comment = extractTrailingPythonLineComment(line);
    const commentStart = comment ? line.lastIndexOf(comment) : -1;
    const codeBeforeComment = commentStart >= 0 ? line.slice(0, commentStart) : line;
    const depthBefore = depth;
    depth = advancePythonDelimiterNestingDepth(depth, codeBeforeComment);
    // Depth 0–1 is the outer call / its kwargs; depth ≥ 2 is nested tuple/list members.
    if (!comment || depthBefore > 1 || seen.has(comment)) continue;
    seen.add(comment);
    comments.push(comment);
  }
  return comments;
}

/**
 * Re-attach safe user trailing comments from the previous binding span onto the generated replacement.
 * Only outer call-arg comments are preserved; mid-tuple/list comments are left behind rather than
 * hoisted onto the regenerated call's closing line.
 */
function withPreservedInlineComments(replacement: string, sourceSpan: string): string {
  const comments = collectInlineCommentsFromBindingSpan(sourceSpan);
  if (comments.length === 0) return replacement;

  const lines = replacement.split('\n');
  const lastIndex = lines.length - 1;
  const lastLine = lines[lastIndex] ?? '';
  if (comments.every((comment) => lastLine.includes(comment))) return replacement;

  const suffix = comments.join(' ');
  lines[lastIndex] = `${lastLine.replace(/\s+$/, '')}  ${suffix}`;
  return lines.join('\n');
}

function callSpanText(lines: string[], range: { startLineIndex: number; endLineIndex: number }): string {
  return lines.slice(range.startLineIndex, range.endLineIndex + 1).join('\n');
}

function toBindingSourceRange(range: { startLineIndex: number; endLineIndex: number }): BindingSourceRange {
  return { startLine: range.startLineIndex + 1, endLine: range.endLineIndex + 1 };
}

function readBindingSourceSpan(model: MonacoEditor.ITextModel, range: BindingSourceRange): string {
  return Array.from({ length: range.endLine - range.startLine + 1 }, (_, index) => model.getLineContent(range.startLine + index)).join('\n');
}

/** Qualify call-start matches so `xdrive_base.foo(` does not match `drive_base.foo(`. */
function qualifiedCallStartRegex(qualifiedName: string): RegExp {
  return new RegExp(String.raw`(?:^|[^\w.])${escapeRegex(qualifiedName)}\s*\(`);
}

function selectAnchoredOrFirstBindingRange(ranges: BindingSourceRange[], binding: InputBinding): BindingSourceRange | null {
  if (ranges.length === 0) return null;
  const anchoredLine = binding.sourceLineNumber;
  // Anchored edits fail closed on sibling retarget: only accept a match that covers the anchor
  // and already passed hybrid identity (+ args when ambiguous). Do not fall back to a weaker
  // button+kind match at the anchored line — that can rewrite hand-authored actions.
  if (anchoredLine !== undefined) {
    return ranges.find((match) => anchoredLine >= match.startLine && anchoredLine <= match.endLine) ?? null;
  }
  return ranges[0] ?? null;
}

function getAxisActionOwningClass(binding: SingleActionInputBinding): FrameworkClass | undefined {
  return binding.action.mechanismClass ?? binding.action.parentClass;
}

function findAxisActionOnClass(owningClass: FrameworkClass, actionName: string): FrameworkAction | undefined {
  return (owningClass.axisActions || []).find((action) => String(action?.name || '') === actionName);
}

/**
 * Resolve axis-action argument schema from the binding's concrete framework class when present.
 * Shared names like `add_variable_velocity_control` differ by class (linear units, source, …).
 */
function findAxisActionArgumentDefs(actionName: string, owningClass?: FrameworkClass | null): FrameworkArgument[] {
  if (owningClass) {
    const action = findAxisActionOnClass(owningClass, actionName);
    return action ? ((action.arguments || []) as FrameworkArgument[]) : [];
  }
  for (const frameworkClass of FrameworkClasses) {
    const action = findAxisActionOnClass(frameworkClass, actionName);
    if (action) return (action.arguments || []) as FrameworkArgument[];
  }
  return [];
}

function findAxisActionArgumentDef(actionName: string, argName: string, owningClass?: FrameworkClass | null): FrameworkArgument | undefined {
  return findAxisActionArgumentDefs(actionName, owningClass).find((argument) => String(argument?.name || '') === argName);
}

/**
 * How framework `defaultValue: null` is treated when matching omitted kwargs.
 * - `exclude-null`: axis path — null is not an omittable default (LED/units enums use non-null defaults).
 * - `include-null`: button path — null is Python `None` and may be omitted from source.
 */
type OmittableNullDefaultPolicy = 'exclude-null' | 'include-null';

function argDefHasOmittableDefault(argDef: FrameworkArgument | undefined, nullPolicy: OmittableNullDefaultPolicy): argDef is FrameworkArgument {
  if (!argDef || argDef.defaultValue === undefined) return false;
  if (nullPolicy === 'exclude-null' && argDef.defaultValue === null) return false;
  return true;
}

/** True when `name=value` matches the framework default and may be omitted from source. */
function isOmittableDefaultArgToken(
  token: string,
  actionName: string,
  findArgDef: (argName: string) => FrameworkArgument | undefined,
  nullPolicy: OmittableNullDefaultPolicy
): boolean {
  const eqIdx = findTopLevelKeywordAssignmentIndex(token);
  if (eqIdx <= 0) return false;
  const argDef = findArgDef(token.slice(0, eqIdx).trim());
  if (!argDefHasOmittableDefault(argDef, nullPolicy)) return false;
  const defaultToken = formatInputBindingActionArgument({ ...argDef, value: argDef.defaultValue }, actionName);
  return normalizePythonCallCompact(token) === normalizePythonCallCompact(defaultToken);
}

/**
 * Reject spans whose explicit kwargs disagree with the binding, including non-default
 * values for args the binding treats as omittable defaults (e.g. ALL vs FRONT targets).
 */
function spanHasConflictingKeywordArgs(
  callText: string,
  expectedArgTokens: readonly string[],
  actionName: string,
  findArgDef: (argName: string) => FrameworkArgument | undefined,
  nullPolicy: OmittableNullDefaultPolicy
): boolean {
  const expectedKeywords = new Map<string, string>();
  for (const token of expectedArgTokens) {
    const eqIdx = findTopLevelKeywordAssignmentIndex(token);
    if (eqIdx <= 0) continue;
    expectedKeywords.set(token.slice(0, eqIdx).trim(), normalizePythonCallCompact(token.slice(eqIdx + 1).trim()));
  }

  for (const [name, spanValue] of extractCallKeywordArgsCompact(callText)) {
    const expectedValue = expectedKeywords.get(name);
    if (expectedValue !== undefined) {
      if (expectedValue !== spanValue) return true;
      continue;
    }
    const argDef = findArgDef(name);
    if (!argDefHasOmittableDefault(argDef, nullPolicy)) return true;
    const defaultToken = formatInputBindingActionArgument({ ...argDef, value: argDef.defaultValue }, actionName);
    const defaultEq = findTopLevelKeywordAssignmentIndex(defaultToken);
    const defaultValue = normalizePythonCallCompact(defaultEq > 0 ? defaultToken.slice(defaultEq + 1).trim() : defaultToken);
    if (spanValue !== defaultValue) return true;
  }
  return false;
}

function extractCallKeywordArgsCompact(callText: string): Map<string, string> {
  const open = callText.indexOf('(');
  const close = callText.lastIndexOf(')');
  const keywords = new Map<string, string>();
  if (open < 0 || close <= open) return keywords;
  for (const token of splitTopLevelByComma(callText.slice(open + 1, close))) {
    const trimmed = token.trim();
    if (!trimmed) continue;
    const eqIdx = findTopLevelKeywordAssignmentIndex(trimmed);
    if (eqIdx <= 0) continue;
    keywords.set(trimmed.slice(0, eqIdx).trim(), normalizePythonCallCompact(trimmed.slice(eqIdx + 1).trim()));
  }
  return keywords;
}

/**
 * Format-tolerant call match: compact equality (or compact token containment), else require a
 * left-bounded callee (when provided) and every required arg token in the span (order-tolerant).
 */
function compactCallOrArgTokensMatch(
  spanText: string,
  expectedCall: string,
  options?: {
    /** Whole-span compact equality (axis mapping lines). Default: treat expected as a nested call token. */
    exactSpanMatch?: boolean;
    /** When set, require this callee's call-start before accepting arg-token membership. */
    actionQualified?: string;
    /** Defaults to all top-level args from `expectedCall`. */
    requiredArgTokens?: readonly string[];
  }
): boolean {
  const compactSpan = normalizePythonCallCompact(spanText);
  const compactExpected = normalizePythonCallCompact(expectedCall);
  if (options?.exactSpanMatch ? compactSpan === compactExpected : compactSpanContainsArgToken(compactSpan, compactExpected)) {
    return true;
  }

  const actionQualified = options?.actionQualified;
  if (actionQualified && !qualifiedCallStartRegex(actionQualified).test(spanText)) return false;

  const open = expectedCall.indexOf('(');
  const close = expectedCall.lastIndexOf(')');
  if (open < 0 || close <= open) {
    // No argument list — only a callee boundary check can succeed.
    return Boolean(actionQualified);
  }

  const requiredTokens =
    options?.requiredArgTokens ??
    splitTopLevelByComma(expectedCall.slice(open + 1, close))
      .map((token) => token.trim())
      .filter(Boolean);
  return requiredTokens.every((token) => compactSpanContainsArgToken(compactSpan, normalizePythonCallCompact(token)));
}

/**
 * Bind call arguments to parameter names using a framework action signature.
 * Positionals fill the next unbound signature slot; keywords may appear in any order after
 * all positionals (Python-style). Returns null when the call shape is invalid.
 */
function resolveCallArgsBySignature(callText: string, signature: readonly FrameworkArgument[]): Map<string, string> | null {
  if (signature.length === 0) return null;
  const open = callText.indexOf('(');
  const close = callText.lastIndexOf(')');
  if (open < 0 || close <= open) return null;

  const resolved = new Map<string, string>();
  let positionalIndex = 0;
  let seenKeyword = false;

  for (const token of splitTopLevelByComma(callText.slice(open + 1, close))) {
    const trimmed = token.trim();
    if (!trimmed) continue;
    const eqIdx = findTopLevelKeywordAssignmentIndex(trimmed);
    if (eqIdx > 0) {
      seenKeyword = true;
      const name = trimmed.slice(0, eqIdx).trim();
      if (!name || resolved.has(name)) return null;
      if (!signature.some((argument) => String(argument?.name || '') === name)) return null;
      resolved.set(name, normalizePythonCallCompact(trimmed.slice(eqIdx + 1).trim()));
      continue;
    }
    if (seenKeyword) return null;
    while (positionalIndex < signature.length && resolved.has(String(signature[positionalIndex]?.name || ''))) {
      positionalIndex += 1;
    }
    if (positionalIndex >= signature.length) return null;
    const name = String(signature[positionalIndex]?.name || '').trim();
    if (!name) return null;
    resolved.set(name, normalizePythonCallCompact(trimmed));
    positionalIndex += 1;
  }

  return resolved;
}

function resolveAxisCallArgsBySignature(callText: string, actionName: string, owningClass?: FrameworkClass | null): Map<string, string> | null {
  return resolveCallArgsBySignature(callText, findAxisActionArgumentDefs(actionName, owningClass));
}

function findBindingActionArgumentDefs(bindingAction: InputBindingAction): FrameworkArgument[] {
  const actionName = String(bindingAction.action?.name || '').trim();
  if (!actionName) return [];

  const owningClass = bindingAction.mechanismClass ?? bindingAction.parentClass;
  const fromOwning = owningClass?.actions?.find((action) => String(action?.name || '') === actionName);
  if (fromOwning?.arguments?.length) return fromOwning.arguments as FrameworkArgument[];

  for (const frameworkClass of FrameworkClasses) {
    const found = (frameworkClass.actions || []).find((action) => String(action?.name || '') === actionName);
    if (found?.arguments?.length) return found.arguments as FrameworkArgument[];
  }

  return (bindingAction.action.arguments || []) as FrameworkArgument[];
}

/** Extract `actionQualified(...)` (balanced) from a larger span such as an `on_pressed(...)` call. */
function extractQualifiedCallSpan(spanText: string, actionQualified: string): string | null {
  const callLineRe = qualifiedCallStartRegex(actionQualified);
  const match = callLineRe.exec(spanText);
  if (!match) return null;

  const openIndex = match.index + match[0].length - 1;
  if (spanText[openIndex] !== '(') return null;
  const closeIndex = findMatchingDelimiterIndex(spanText, openIndex, '(', ')');
  if (closeIndex < 0) return null;

  const nameStart = spanText.indexOf(actionQualified, match.index);
  if (nameStart < 0 || nameStart > openIndex) return null;
  return spanText.slice(nameStart, closeIndex + 1);
}

/** True when the axis call span has this binding's action callee (left-bounded). Arg values ignored. */
function variableAxisSpanMatchesActionIdentity(spanText: string, binding: SingleActionInputBinding): boolean {
  const { actionQualified } = buildActionQualified(binding.action);
  if (!actionQualified) return false;
  return qualifiedCallStartRegex(actionQualified).test(spanText);
}

/**
 * True when the source axis span matches this binding's args (format-tolerant), including
 * omitted framework defaults, kwarg order, and keyword-vs-positional forms.
 */
function variableAxisSpanMatchesArgs(spanText: string, varName: string, binding: SingleActionInputBinding): boolean {
  const expected = buildDirectBindingLine(varName, binding);
  if (!expected) return false;

  const open = expected.indexOf('(');
  const close = expected.lastIndexOf(')');
  if (open < 0 || close <= open) return false;
  const actionName = binding.action.action.name;
  const owningClass = getAxisActionOwningClass(binding);
  const argTokens = splitTopLevelByComma(expected.slice(open + 1, close))
    .map((token) => token.trim())
    .filter(Boolean);
  const findArgDef = (argName: string) => findAxisActionArgumentDef(actionName, argName, owningClass);
  if (spanHasConflictingKeywordArgs(spanText, argTokens, actionName, findArgDef, 'exclude-null')) return false;
  const requiredTokens = argTokens.filter((token) => !isOmittableDefaultArgToken(token, actionName, findArgDef, 'exclude-null'));

  if (compactCallOrArgTokensMatch(spanText, expected, { exactSpanMatch: true, requiredArgTokens: requiredTokens })) {
    return true;
  }

  const resolvedArgs = resolveAxisCallArgsBySignature(spanText, actionName, owningClass);
  if (!resolvedArgs) return false;
  return requiredTokens.every((token) => {
    const eqIdx = findTopLevelKeywordAssignmentIndex(token);
    if (eqIdx <= 0) return false;
    const name = token.slice(0, eqIdx).trim();
    const expectedValue = normalizePythonCallCompact(token.slice(eqIdx + 1).trim());
    return resolvedArgs.get(name) === expectedValue;
  });
}

/** True when `spanText` contains this binding's action identity (left-bounded callee). */
function bindingActionMatchesIdentity(spanText: string, bindingAction: InputBindingAction): boolean {
  if (!buildActionCall(bindingAction)) return false;
  const { actionQualified, isCustomAction } = buildActionQualified(bindingAction);
  if (!actionQualified) return false;
  if (qualifiedCallStartRegex(actionQualified).test(spanText)) return true;
  if (isCustomAction) {
    return compactSpanContainsArgToken(normalizePythonCallCompact(spanText), normalizePythonCallCompact(actionQualified));
  }
  return false;
}

/**
 * True when `spanText` contains this binding action's full call (callee + args).
 * Keyword order may differ; callee prefixes like `stop_action_extra` must not match `stop_action`.
 * Framework defaults present on the UI binding but omitted in source still match (axis-style).
 */
function bindingActionMatchesArgs(spanText: string, bindingAction: InputBindingAction): boolean {
  const expectedCall = buildActionCall(bindingAction);
  if (!expectedCall) return false;
  const { actionQualified, isCustomAction } = buildActionQualified(bindingAction);
  if (isCustomAction) {
    return compactSpanContainsArgToken(normalizePythonCallCompact(spanText), normalizePythonCallCompact(expectedCall));
  }
  if (!actionQualified) return false;

  const open = expectedCall.indexOf('(');
  const close = expectedCall.lastIndexOf(')');
  if (open < 0 || close <= open) {
    return qualifiedCallStartRegex(actionQualified).test(spanText);
  }

  const actionName = String(bindingAction.action?.name || '');
  const signature = findBindingActionArgumentDefs(bindingAction);
  const findArgDef = (argName: string) => signature.find((argument) => String(argument?.name || '') === argName);
  const argTokens = splitTopLevelByComma(expectedCall.slice(open + 1, close))
    .map((token) => token.trim())
    .filter(Boolean);
  const nestedCall = extractQualifiedCallSpan(spanText, actionQualified);
  const callTextForArgs = nestedCall ?? spanText;
  if (spanHasConflictingKeywordArgs(callTextForArgs, argTokens, actionName, findArgDef, 'include-null')) return false;

  const requiredTokens = argTokens.filter((token) => !isOmittableDefaultArgToken(token, actionName, findArgDef, 'include-null'));
  if (compactCallOrArgTokensMatch(spanText, expectedCall, { actionQualified, requiredArgTokens: requiredTokens })) {
    return true;
  }

  if (!nestedCall) return false;
  const resolvedArgs = resolveCallArgsBySignature(nestedCall, signature);
  if (!resolvedArgs) return false;
  return requiredTokens.every((token) => {
    const eqIdx = findTopLevelKeywordAssignmentIndex(token);
    if (eqIdx <= 0) {
      return compactSpanContainsArgToken(normalizePythonCallCompact(nestedCall), normalizePythonCallCompact(token));
    }
    const name = token.slice(0, eqIdx).trim();
    const expectedValue = normalizePythonCallCompact(token.slice(eqIdx + 1).trim());
    return resolvedArgs.get(name) === expectedValue;
  });
}

/**
 * Keep all identity hits when there is only one (loose: stale args still match).
 * When multiple identity hits exist, require arg-value agreement to disambiguate siblings.
 */
function refineRangesIfAmbiguous(
  identityRanges: Array<{ startLineIndex: number; endLineIndex: number }>,
  lines: string[],
  matchesArgs: (spanText: string) => boolean
): Array<{ startLineIndex: number; endLineIndex: number }> {
  if (identityRanges.length <= 1) return identityRanges;
  return identityRanges.filter((range) => matchesArgs(callSpanText(lines, range)));
}

/** Axis mapping spans: loose on a single action hit; arg-aware when siblings share action identity. */
function findVariableAxisBindingRanges(
  model: MonacoEditor.ITextModel,
  varName: string,
  binding: SingleActionInputBinding,
  options?: { identityOnly?: boolean; ir?: TreeSitterInputBindingIr | null }
): BindingSourceRange[] {
  const getter = getAxisGetterForButton(binding.button);
  const { actionQualified } = buildActionQualified(binding.action);
  if (!getter || !actionQualified) return [];

  // Tree-sitter only — no scanner/regex fallback for axis range lookup.
  if (!isPythonTreeSitterReady()) return [];

  const lines = getTextModelLines(model);
  const treeRanges = findVariableAxisBindingLineRangesSync(
    model.getValue(),
    varName,
    {
      actionQualified,
      getterName: getter,
    },
    options?.ir
  );
  const identityRanges = treeRanges
    .map((range) => ({ startLineIndex: range.startLine - 1, endLineIndex: range.endLine - 1 }))
    .filter((range) => variableAxisSpanMatchesActionIdentity(callSpanText(lines, range), binding));

  if (options?.identityOnly) return identityRanges.map(toBindingSourceRange);

  return refineRangesIfAmbiguous(identityRanges, lines, (spanText) => variableAxisSpanMatchesArgs(spanText, varName, binding)).map(
    toBindingSourceRange
  );
}

function findButtonTriggerBindingRanges(
  model: MonacoEditor.ITextModel,
  varName: string,
  binding: InputBinding,
  options?: { identityOnly?: boolean; ir?: TreeSitterInputBindingIr | null }
): BindingSourceRange[] {
  if (isUnsupportedInputBinding(binding)) return [];

  const triggerToken = binding.kind === INPUT_BINDING_KIND.TOGGLE ? 'on_toggle' : bindingMethod(binding);
  if (!triggerToken) return [];

  if (binding.kind === INPUT_BINDING_KIND.TOGGLE) {
    if (!buildActionCall(binding.onAction) || !buildActionCall(binding.offAction)) return [];
  } else if (!buildActionCall(binding.action)) {
    return [];
  }

  // Tree-sitter only — no scanner/regex fallback for button trigger range lookup.
  if (!isPythonTreeSitterReady()) return [];

  const lines = getTextModelLines(model);
  const treeRanges = findButtonTriggerBindingLineRangesSync(
    model.getValue(),
    varName,
    {
      buttonName: binding.button,
      triggerToken,
    },
    options?.ir
  );

  const identityRanges = treeRanges
    .map((range) => ({ startLineIndex: range.startLine - 1, endLineIndex: range.endLine - 1 }))
    .filter((range) => {
      const spanText = callSpanText(lines, range);
      if (binding.kind === INPUT_BINDING_KIND.TOGGLE) {
        return bindingActionMatchesIdentity(spanText, binding.onAction) && bindingActionMatchesIdentity(spanText, binding.offAction);
      }
      return bindingActionMatchesIdentity(spanText, binding.action);
    });

  if (options?.identityOnly) return identityRanges.map(toBindingSourceRange);

  return refineRangesIfAmbiguous(identityRanges, lines, (spanText) => {
    if (binding.kind === INPUT_BINDING_KIND.TOGGLE) {
      return bindingActionMatchesArgs(spanText, binding.onAction) && bindingActionMatchesArgs(spanText, binding.offAction);
    }
    return bindingActionMatchesArgs(spanText, binding.action);
  }).map(toBindingSourceRange);
}

/**
 * Hybrid match for delete: sole identity hit is accepted even with stale args; multiple
 * identity hits are disambiguated by parameter values.
 */
function findSemanticDirectBindingRanges(
  model: MonacoEditor.ITextModel,
  varName: string,
  binding: InputBinding,
  ir?: TreeSitterInputBindingIr | null
): BindingSourceRange[] {
  if (isUnsupportedInputBinding(binding)) return [];
  if (binding.kind === INPUT_BINDING_KIND.VARIABLE) {
    return findVariableAxisBindingRanges(model, varName, binding, { ir });
  }
  return findButtonTriggerBindingRanges(model, varName, binding, { ir });
}

/**
 * All same-identity call spans (args ignored). Used by replace to collapse invalid
 * same-button/trigger/action siblings into one call with the new UI values.
 */
function findIdentityDirectBindingRanges(model: MonacoEditor.ITextModel, varName: string, binding: InputBinding): BindingSourceRange[] {
  if (isUnsupportedInputBinding(binding)) return [];
  if (binding.kind === INPUT_BINDING_KIND.VARIABLE) {
    return findVariableAxisBindingRanges(model, varName, binding, { identityOnly: true });
  }
  return findButtonTriggerBindingRanges(model, varName, binding, { identityOnly: true });
}

/**
 * Top-level same-identity peers collapse on edit. Nested/indented spans only replace the
 * primary — same indent under different `if` blocks must not wipe each other.
 */
function peerBindingRanges(model: MonacoEditor.ITextModel, primary: BindingSourceRange, ranges: readonly BindingSourceRange[]): BindingSourceRange[] {
  const primaryIndent = /^(\s*)/.exec(model.getLineContent(primary.startLine))?.[1] ?? '';
  if (primaryIndent.length > 0) return [primary];
  return ranges.filter((range) => {
    const indent = /^(\s*)/.exec(model.getLineContent(range.startLine))?.[1] ?? '';
    return indent.length === 0;
  });
}

function isTopLevelBindingRange(model: MonacoEditor.ITextModel, range: BindingSourceRange): boolean {
  const indent = /^(\s*)/.exec(model.getLineContent(range.startLine))?.[1] ?? '';
  return indent.length === 0;
}

/**
 * Call spans for this binding's button + kind/trigger (action ignored).
 * Used only by the on_toggle swap wipe during button-dialog save.
 */
function findButtonKindRanges(model: MonacoEditor.ITextModel, varName: string, binding: InputBinding): BindingSourceRange[] {
  if (isUnsupportedInputBinding(binding)) return [];
  if (!isPythonTreeSitterReady()) return [];

  if (binding.kind === INPUT_BINDING_KIND.VARIABLE) {
    const getter = getAxisGetterForButton(binding.button);
    if (!getter) return [];
    return findAxisBindingLineRangesForGetterSync(model.getValue(), varName, getter).map((range) => ({
      startLine: range.startLine,
      endLine: range.endLine,
    }));
  }

  const triggerToken = binding.kind === INPUT_BINDING_KIND.TOGGLE ? 'on_toggle' : bindingMethod(binding);
  if (!triggerToken) return [];
  return findButtonTriggerBindingLineRangesSync(model.getValue(), varName, {
    buttonName: binding.button,
    triggerToken,
  }).map((range) => ({ startLine: range.startLine, endLine: range.endLine }));
}

/**
 * Top-level call spans for this binding's button + kind/trigger (action ignored).
 * Used only for the on_toggle swap wipe during button-dialog save — not for stale-anchor
 * replace/delete, which must stay identity-fail-closed.
 */
function findTopLevelButtonKindRanges(model: MonacoEditor.ITextModel, varName: string, binding: InputBinding): BindingSourceRange[] {
  return findButtonKindRanges(model, varName, binding).filter((range) => isTopLevelBindingRange(model, range));
}

/**
 * Delete every top-level same button+kind peer (action-agnostic). Nested/indented
 * copies are left alone. Returns `{ ok: true, changed: false }` when none match.
 */
function wipeTopLevelButtonKindPeersFromCode(editor: MonacoEditor.IStandaloneCodeEditor, binding: InputBinding): CodeMutationResult {
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  const model = targetEditor.getModel();
  if (!model) return { ok: false, reason: 'No editor model available.' };
  if (!isPythonTreeSitterReady()) {
    return treeSitterNotReadyResult('delete');
  }

  const isDrivebase = binding.device === DRIVEBASE;
  const varName = resolveBindingReceiverVarName(model, binding);
  const ranges = findTopLevelButtonKindRanges(model, varName, binding);
  if (ranges.length === 0) return { ok: true, changed: false };

  const linesToDelete = deletedLineNumbersFromRanges(ranges);
  const edits: MonacoEditor.IIdentifiedSingleEditOperation[] = [...linesToDelete]
    .sort((left, right) => right - left)
    .map((lineNumber) => ({
      range: new Range(lineNumber, 1, lineNumber + 1, 1),
      text: '',
      forceMoveMarkers: true,
    }));
  deleteAssignmentIfLastMapping(model, edits, varName, isDrivebase ? 'ActionDriveBasePanel' : 'ActionController', !isDrivebase);
  return executeEdits(targetEditor, isDrivebase ? 'drivebase.mapping.delete' : 'controller.mapping.delete', edits);
}

function deletedLineNumbersFromRanges(ranges: readonly BindingSourceRange[]): number[] {
  const lineNumbers = new Set<number>();
  for (const range of ranges) {
    for (let lineNumber = range.startLine; lineNumber <= range.endLine; lineNumber++) {
      lineNumbers.add(lineNumber);
    }
  }
  return [...lineNumbers];
}

function earliestEditStartLine(edits: readonly MonacoEditor.IIdentifiedSingleEditOperation[]): number {
  return Math.min(...edits.map((edit) => edit.range.startLineNumber));
}

function findInputPanelDeclarationLineIndex(
  lines: string[],
  varName: string,
  className: 'ActionController' | 'ActionDriveBasePanel',
  beforeLineNumber?: number
): number | null {
  const beforeLineIndex = typeof beforeLineNumber === 'number' ? beforeLineNumber - 1 : undefined;
  if (beforeLineIndex !== undefined) {
    return findVarAssignmentLineIndex(lines, varName, {
      allowedClassNames: new Set([className]),
      beforeLineIndex,
    });
  }
  const assignments = collectTopLevelVarAssignments(lines, {
    allowedClassNames: new Set([className]),
  });
  const matchingAssignments = assignments.filter(
    (assignment) => assignment.variableName === varName && (beforeLineIndex === undefined || assignment.lineIndex < beforeLineIndex)
  );
  return matchingAssignments.at(-1)?.lineIndex ?? null;
}

/**
 * When the deleted span was the receiver's last mapping, also remove the panel declaration.
 *
 * Evaluate "any mappings left?" against the source **after** the binding delete edits, not the
 * pre-delete text with whole lines ignored. Partial-span deletes on a semicolon suite leave a
 * neighbour on the same physical line; line-based ignore would treat that neighbour as gone and
 * drop `controller = ActionController()` while the surviving call still needs it.
 */
function deleteAssignmentIfLastMapping(
  model: MonacoEditor.ITextModel,
  edits: MonacoEditor.IIdentifiedSingleEditOperation[],
  varName: string,
  className: 'ActionController' | 'ActionDriveBasePanel',
  includeAxisMappings: boolean
) {
  if (edits.length === 0) return;

  const codeAfterBindingDelete = applyCodeEdits(getCodeTextFromModel(model), edits);
  if (
    hasRemainingInputBindingsForReceiverSync(codeAfterBindingDelete, varName, {
      includeAxisMappings,
      ignoreLineNumbers: new Set(),
    })
  ) {
    return;
  }

  const lines = getTextModelLines(model);
  const earliestDeletedLine = earliestEditStartLine(edits);
  if (Number.isFinite(earliestDeletedLine) && hasTopLevelVarAssignmentAfterLine(lines, varName, earliestDeletedLine - 1)) return;
  const declLineIndex = findInputPanelDeclarationLineIndex(
    lines,
    varName,
    className,
    Number.isFinite(earliestDeletedLine) ? earliestDeletedLine : undefined
  );
  if (declLineIndex === null) return;
  const endLineIndex = findConstructorCallEndLineIndex(lines, declLineIndex);
  edits.push({ range: new Range(declLineIndex + 1, 1, endLineIndex + 2, 1), text: '', forceMoveMarkers: true });
}

function executeEdits(
  editor: MonacoEditor.IStandaloneCodeEditor,
  source: string,
  edits: MonacoEditor.IIdentifiedSingleEditOperation[]
): CodeMutationResult {
  if (!edits.length) return { ok: true, changed: false };
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  targetEditor.pushUndoStop();
  targetEditor.executeEdits(source, edits);
  removeEmptyInputMappingsRegionIfNeeded(targetEditor);
  targetEditor.pushUndoStop();
  targetEditor.focus();
  return { ok: true, changed: true };
}

function getCodeTextFromModel(model: MonacoEditor.ITextModel): string {
  if (typeof model.getValue === 'function') return model.getValue();
  return getTextModelLines(model).join('\n');
}

function getLineMaxColumn(model: MonacoEditor.ITextModel, lineNumber: number): number {
  if (typeof model.getLineMaxColumn === 'function') return model.getLineMaxColumn(lineNumber);
  return model.getLineContent(lineNumber).length + 1;
}

function createTextModelFromCode(code: string): MonacoEditor.ITextModel {
  const lines = splitCodeLines(code);
  return {
    getValue: () => code,
    getLineCount: () => lines.length,
    getLineContent: (lineNumber: number) => lines[lineNumber - 1] ?? '',
    getLineMaxColumn: (lineNumber: number) => (lines[lineNumber - 1] ?? '').length + 1,
    findMatches: (searchString: string, _searchOnlyEditableRange?: boolean, isRegex?: boolean) => {
      const matcher = isRegex ? new RegExp(searchString) : null;
      return lines.flatMap((line, index) => {
        const matched = matcher ? matcher.test(line) : line.includes(searchString);
        return matched ? [{ range: new Range(index + 1, 1, index + 1, line.length + 1) }] : [];
      });
    },
  } as unknown as MonacoEditor.ITextModel;
}

function createTransientCodeEditor(code: string): {
  editor: MonacoEditor.IStandaloneCodeEditor;
  getCode: () => string;
} {
  let currentCode = code;
  let currentModel = createTextModelFromCode(currentCode);

  const updateCode = (nextCode: string) => {
    currentCode = nextCode;
    currentModel = createTextModelFromCode(currentCode);
  };

  const editor = {
    getModel: () => currentModel,
    executeEdits: (_source: string, edits: MonacoEditor.IIdentifiedSingleEditOperation[]) => {
      updateCode(applyCodeEdits(currentCode, edits));
      return true;
    },
    pushUndoStop: () => true,
    focus: () => undefined,
  } as MonacoEditor.IStandaloneCodeEditor;

  return {
    editor,
    getCode: () => currentCode,
  };
}

function getCodeLineStartOffsets(code: string): number[] {
  const lineStarts = [0];
  for (let index = 0; index < code.length; index++) {
    if (code[index] === '\n') lineStarts.push(index + 1);
  }
  return lineStarts;
}

function positionToCodeOffset(code: string, lineNumber: number, column: number): number {
  const lines = splitCodeLines(code);
  const lineStarts = getCodeLineStartOffsets(code);
  const normalizedLine = Math.max(1, Math.min(lineNumber, lineStarts.length + 1));
  const normalizedColumn = Math.max(1, column);
  if (normalizedLine === lineStarts.length + 1) {
    return code.length + (normalizedColumn - 1);
  }

  const offset = lineStarts[normalizedLine - 1] ?? code.length;
  const line = lines[normalizedLine - 1] ?? '';
  return offset + Math.min(normalizedColumn - 1, line.length);
}

function shouldPrefixVirtualLineNewline(code: string, edit: MonacoEditor.IIdentifiedSingleEditOperation): boolean {
  const startLine = edit.range.startLineNumber;
  const startColumn = edit.range.startColumn;
  const lines = splitCodeLines(code);
  return (
    startLine > lines.length &&
    startColumn === 1 &&
    edit.range.endLineNumber === startLine &&
    edit.range.endColumn === startColumn &&
    code.length > 0 &&
    !code.endsWith('\n')
  );
}

function applyCodeEdits(code: string, edits: readonly MonacoEditor.IIdentifiedSingleEditOperation[]): string {
  return edits
    .slice()
    .sort(
      (left, right) =>
        positionToCodeOffset(code, right.range.startLineNumber, right.range.startColumn) -
        positionToCodeOffset(code, left.range.startLineNumber, left.range.startColumn)
    )
    .reduce((nextCode, edit) => {
      const start = positionToCodeOffset(nextCode, edit.range.startLineNumber, edit.range.startColumn);
      const end = positionToCodeOffset(nextCode, edit.range.endLineNumber, edit.range.endColumn);
      const newlinePrefix = shouldPrefixVirtualLineNewline(nextCode, edit) ? '\n' : '';
      return `${nextCode.slice(0, start)}${newlinePrefix}${edit.text ?? ''}${nextCode.slice(end)}`;
    }, code);
}

function buildFullModelReplaceEdit(model: MonacoEditor.ITextModel, text: string): MonacoEditor.IIdentifiedSingleEditOperation {
  const lineCount = Math.max(model.getLineCount(), 1);
  return {
    range: new Range(1, 1, lineCount, getLineMaxColumn(model, lineCount)),
    text,
    forceMoveMarkers: true,
  };
}

function executeAtomicCodeMutation(
  editor: MonacoEditor.IStandaloneCodeEditor,
  source: string,
  mutateCode: (model: MonacoEditor.ITextModel, code: string) => string | null
): CodeMutationResult {
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  const model = targetEditor.getModel();
  if (!model) return { ok: false, reason: 'No editor model available.' };
  const currentCode = getCodeTextFromModel(model);
  const nextCode = mutateCode(model, currentCode);
  if (nextCode === null || nextCode === currentCode) return { ok: true, changed: false };
  return executeEdits(targetEditor, source, [buildFullModelReplaceEdit(model, nextCode)]);
}

type AtomicBindingCodeResult =
  | {
      ok: true;
      code: string;
    }
  | {
      ok: false;
      reason: string;
    };

function executeAtomicBindingCodeMutation(
  editor: MonacoEditor.IStandaloneCodeEditor,
  source: string,
  mutateCode: (model: MonacoEditor.ITextModel, code: string) => AtomicBindingCodeResult
): CodeMutationResult {
  let failureResult: Extract<CodeMutationResult, { ok: false }> | null = null;
  const result = executeAtomicCodeMutation(editor, source, (model, code) => {
    const nextCode = mutateCode(model, code);
    if (!nextCode.ok) {
      failureResult = { ok: false, reason: nextCode.reason };
      return null;
    }
    return nextCode.code;
  });
  return failureResult ?? result;
}

function getInputMappingsRegionInsertLine(model: MonacoEditor.ITextModel): number {
  return getInsertLineBeforeRobotStart(model) ?? model.getLineCount() + 1;
}

function buildInputMappingsRegionText(lines: string[]): string {
  return `${INPUT_MAPPINGS_REGION_START}\n${lines.join('\n')}\n${REGION_END}\n\n`;
}

function getDetachedInputPanelBindingInsertLine(model: MonacoEditor.ITextModel, binding: InputBinding): number {
  const baseInsertLine = getInputMappingsRegionInsertLine(model);
  return bumpInsertLineForTopLevelVarDeclarationAnchors(model, baseInsertLine, collectActionTargetDeclarationAnchors(binding));
}

function getSingleTransactionControllerDeclarationEdits(model: MonacoEditor.ITextModel): MonacoEditor.IIdentifiedSingleEditOperation[] {
  const regionBounds = findInputMappingsRegionBlockBounds(model);
  if (regionBounds) {
    return [
      {
        range: new Range(regionBounds.startLine + 1, 1, regionBounds.startLine + 1, 1),
        text: 'controller = ActionController()\n',
        forceMoveMarkers: true,
      },
    ];
  }

  const insertLine = getInputMappingsRegionInsertLine(model);
  return [
    {
      range: new Range(insertLine, 1, insertLine, 1),
      text: buildInputMappingsRegionText(['controller = ActionController()']),
      forceMoveMarkers: true,
    },
  ];
}

function getSingleTransactionDrivebaseDeclarationEdits(model: MonacoEditor.ITextModel): MonacoEditor.IIdentifiedSingleEditOperation[] {
  const regionBounds = findInputMappingsRegionBlockBounds(model);
  const controllerDeclInsertLine = getInsertLineAfterInputPanelDeclaration(model, 'ActionController', 'controller');

  if (controllerDeclInsertLine !== null) {
    return [
      {
        range: new Range(controllerDeclInsertLine, 1, controllerDeclInsertLine, 1),
        text: 'drivebase_panel = ActionDriveBasePanel()\n',
        forceMoveMarkers: true,
      },
    ];
  }

  if (regionBounds) {
    return [
      {
        range: new Range(regionBounds.startLine + 1, 1, regionBounds.startLine + 1, 1),
        text: 'drivebase_panel = ActionDriveBasePanel()\n',
        forceMoveMarkers: true,
      },
    ];
  }

  const insertLine = getInputMappingsRegionInsertLine(model);
  return [
    {
      range: new Range(insertLine, 1, insertLine, 1),
      text: buildInputMappingsRegionText(['drivebase_panel = ActionDriveBasePanel()']),
      forceMoveMarkers: true,
    },
  ];
}

function buildFirstInputPanelBindingCode(
  model: MonacoEditor.ITextModel,
  declarationEdits: readonly MonacoEditor.IIdentifiedSingleEditOperation[],
  varName: string,
  binding: InputBinding,
  newLine: string
): AtomicBindingCodeResult {
  const codeAfterDeclaration = applyCodeEdits(getCodeTextFromModel(model), declarationEdits);
  const nextModel = createTextModelFromCode(codeAfterDeclaration);
  const insertLineResult = resolveInsertLineForInputPanelBinding(nextModel, varName, binding.device, binding);
  if (!insertLineResult.ok) return { ok: false, reason: insertLineResult.reason };
  const code = applyCodeEdits(codeAfterDeclaration, [
    {
      range: new Range(insertLineResult.insertLine, 1, insertLineResult.insertLine, 1),
      text: `${newLine}\n`,
      forceMoveMarkers: true,
    },
  ]);
  return { ok: true, code };
}

function buildInsertedBindingCode(
  model: MonacoEditor.ITextModel,
  varName: string,
  device: InputBinding['device'],
  binding: InputBinding,
  newLine: string,
  ownerLineNumber?: number,
  insertLineOverride?: number
): AtomicBindingCodeResult {
  const insertLineResult =
    insertLineOverride === undefined ? resolveInsertLineForInputPanelBinding(model, varName, device, binding, ownerLineNumber) : null;
  if (insertLineResult && !insertLineResult.ok) return { ok: false, reason: insertLineResult.reason };
  const insertLine = insertLineOverride ?? insertLineResult?.insertLine;
  if (insertLine === undefined) return { ok: false, reason: 'Could not determine mapping insert line.' };
  let nextCode = applyCodeEdits(getCodeTextFromModel(model), [
    {
      range: new Range(insertLine, 1, insertLine, 1),
      text: `${newLine}\n`,
      forceMoveMarkers: true,
    },
  ]);
  const cleanupModel = createTextModelFromCode(nextCode);
  const cleanupEdit = getRemoveEmptyInputMappingsRegionEdit(cleanupModel);
  if (cleanupEdit) {
    nextCode = applyCodeEdits(nextCode, [cleanupEdit]);
  }
  return { ok: true, code: nextCode };
}

function insertControllerBinding(editor: MonacoEditor.IStandaloneCodeEditor, binding: InputBinding): CodeMutationResult {
  // Tree-sitter owns insert placement; fail closed when not ready (no after-decl fallback write).
  if (!isPythonTreeSitterReady()) {
    return treeSitterNotReadyResult('insert');
  }
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  const model = targetEditor.getModel();
  if (!model) return { ok: false, reason: 'No editor model available.' };
  const hasController = hasTopLevelInputPanelDeclaration(model, 'ActionController');
  const controllerVarName = hasController ? resolveBindingReceiverVarName(model, binding) : 'controller';
  const newLine = buildFormattedDirectBindingLine(controllerVarName, binding);
  if (!newLine) return { ok: true, changed: false };
  if (!hasController) {
    return executeAtomicBindingCodeMutation(targetEditor, 'controller.mapping', (currentModel) =>
      buildFirstInputPanelBindingCode(currentModel, getSingleTransactionControllerDeclarationEdits(currentModel), controllerVarName, binding, newLine)
    );
  }
  const existingEmptyRegion = getRemoveEmptyInputMappingsRegionEdit(model);
  if (existingEmptyRegion) {
    const detachedInsertLine = getDetachedInputPanelBindingInsertLine(model, binding);
    return executeAtomicBindingCodeMutation(targetEditor, 'controller.mapping', (currentModel) =>
      buildInsertedBindingCode(currentModel, controllerVarName, 'controller', binding, newLine, undefined, detachedInsertLine)
    );
  }
  const insertLineResult = resolveInsertLineForInputPanelBinding(model, controllerVarName, 'controller', binding);
  if (!insertLineResult.ok) return { ok: false, reason: insertLineResult.reason };
  return executeEdits(targetEditor, 'controller.mapping', [
    {
      range: new Range(insertLineResult.insertLine, 1, insertLineResult.insertLine, 1),
      text: newLine + '\n',
      forceMoveMarkers: true,
    },
  ]);
}

function insertDrivebaseBinding(editor: MonacoEditor.IStandaloneCodeEditor, binding: InputBinding): CodeMutationResult {
  // Tree-sitter owns insert placement; fail closed when not ready (no after-decl fallback write).
  if (!isPythonTreeSitterReady()) {
    return treeSitterNotReadyResult('insert');
  }
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  const model = targetEditor.getModel();
  if (!model) return { ok: false, reason: 'No editor model available.' };
  const hasDrivebasePanel = hasTopLevelInputPanelDeclaration(model, 'ActionDriveBasePanel');
  const drivebaseVarName = hasDrivebasePanel ? resolveBindingReceiverVarName(model, binding) : 'drivebase_panel';
  const newLine = buildFormattedDirectBindingLine(drivebaseVarName, binding);
  if (!newLine) return { ok: true, changed: false };
  if (!hasDrivebasePanel) {
    return executeAtomicBindingCodeMutation(targetEditor, 'drivebase.mapping', (currentModel) =>
      buildFirstInputPanelBindingCode(currentModel, getSingleTransactionDrivebaseDeclarationEdits(currentModel), drivebaseVarName, binding, newLine)
    );
  }
  const insertLineResult = resolveInsertLineForInputPanelBinding(model, drivebaseVarName, 'drivebase', binding);
  if (!insertLineResult.ok) return { ok: false, reason: insertLineResult.reason };
  return executeEdits(targetEditor, 'drivebase.mapping', [
    {
      range: new Range(insertLineResult.insertLine, 1, insertLineResult.insertLine, 1),
      text: newLine + '\n',
      forceMoveMarkers: true,
    },
  ]);
}

function buildRelocatedBindingCode(
  model: MonacoEditor.ITextModel,
  deleteEdits: readonly MonacoEditor.IIdentifiedSingleEditOperation[],
  varName: string,
  device: InputBinding['device'],
  nextBinding: InputBinding,
  newLine: string,
  sourceLineNumber?: number
): AtomicBindingCodeResult {
  const codeAfterDelete = applyCodeEdits(getCodeTextFromModel(model), deleteEdits);
  const nextModel = createTextModelFromCode(codeAfterDelete);
  const insertLineResult = resolveInsertLineForInputPanelBinding(nextModel, varName, device, nextBinding, sourceLineNumber);
  if (!insertLineResult.ok) return { ok: false, reason: insertLineResult.reason };
  const code = applyCodeEdits(codeAfterDelete, [
    {
      range: new Range(insertLineResult.insertLine, 1, insertLineResult.insertLine, 1),
      text: newLine + '\n',
      forceMoveMarkers: true,
    },
  ]);
  return { ok: true, code };
}

export function deleteInputBindingFromCode(editor: MonacoEditor.IStandaloneCodeEditor, binding: InputBinding): CodeMutationResult {
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  const model = targetEditor.getModel();
  if (!model) return { ok: false, reason: 'No editor model available.' };
  // Tree-sitter owns span selection; fail closed when not ready (do not soft-no-op —
  // callers like saveButtonBindings would otherwise commit Redux without editing Main.py).
  if (!isPythonTreeSitterReady()) {
    return treeSitterNotReadyResult('delete');
  }
  const isDrivebase = binding.device === DRIVEBASE;
  const varName = resolveBindingReceiverVarName(model, binding);

  if (isUnsupportedInputBinding(binding)) {
    const deleteEdit = resolveUnsupportedBindingDeleteEditSync(model.getValue(), varName, {
      rawCode: String(binding.rawCode || ''),
      buttonName: binding.button,
      triggerToken: inputBindingTriggerMethod(binding),
      sourceLineNumber: binding.sourceLineNumber,
    });
    if (!deleteEdit) {
      // Fall back to whole-line match when the structured delete helper cannot resolve a span.
      const existingRange = matchUnsupportedRawSpan(model, binding);
      if (!existingRange) return { ok: false, reason: 'Unsupported mapping source could not be matched for deletion.' };
      const edits: MonacoEditor.IIdentifiedSingleEditOperation[] = [
        { range: new Range(existingRange.startLine, 1, existingRange.endLine + 1, 1), text: '', forceMoveMarkers: true },
      ];
      deleteAssignmentIfLastMapping(model, edits, varName, isDrivebase ? 'ActionDriveBasePanel' : 'ActionController', !isDrivebase);
      return executeEdits(targetEditor, isDrivebase ? 'drivebase.mapping.delete' : 'controller.mapping.delete', edits);
    }

    const edits: MonacoEditor.IIdentifiedSingleEditOperation[] = [
      {
        range: new Range(deleteEdit.startLine, deleteEdit.startColumn, deleteEdit.endLine, deleteEdit.endColumn),
        text: deleteEdit.text,
        forceMoveMarkers: true,
      },
    ];
    deleteAssignmentIfLastMapping(model, edits, varName, isDrivebase ? 'ActionDriveBasePanel' : 'ActionController', !isDrivebase);
    return executeEdits(targetEditor, isDrivebase ? 'drivebase.mapping.delete' : 'controller.mapping.delete', edits);
  }

  // Tree-sitter owns span selection for supported bindings; no scanner/exact fallback.
  const semanticRanges = findSemanticDirectBindingRanges(model, varName, binding);
  const semanticRange = selectAnchoredOrFirstBindingRange(semanticRanges, binding);
  if (!semanticRange) {
    // TODO(BPS-800 follow-up): distinguish truly absent mappings from present-but-unmatched source
    // (unexpected formatting, e.g. multiline positional button args). Control only clears the row when
    // `changed` is true, so unmatched deletes keep the UI row for now.
    return { ok: true, changed: false };
  }
  // Delete removes only the primary span (anchored or first hybrid match). Edit/replace owns
  // top-level peer collapse — unanchored duplicates are left for a later delete or edit.
  const linesToDelete = deletedLineNumbersFromRanges([semanticRange]);

  const edits: MonacoEditor.IIdentifiedSingleEditOperation[] = [...linesToDelete]
    .sort((left, right) => right - left)
    .map((lineNumber) => ({
      range: new Range(lineNumber, 1, lineNumber + 1, 1),
      text: '',
      forceMoveMarkers: true,
    }));
  deleteAssignmentIfLastMapping(model, edits, varName, isDrivebase ? 'ActionDriveBasePanel' : 'ActionController', !isDrivebase);
  return executeEdits(targetEditor, isDrivebase ? 'drivebase.mapping.delete' : 'controller.mapping.delete', edits);
}

export function upsertInputBindingInCode(
  editor: MonacoEditor.IStandaloneCodeEditor,
  binding: InputBinding,
  previousBinding?: InputBinding | null
): CodeMutationResult {
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  const nextBindingWithReceiver =
    previousBinding && previousBinding.device === binding.device && getStoredReceiverVarName(previousBinding)
      ? withBindingReceiverVarName(binding, previousBinding.receiverVarName)
      : binding;
  if (previousBinding) {
    const replaceResult = replaceBindingInCode(targetEditor, previousBinding, nextBindingWithReceiver);
    if (replaceResult.ok) return replaceResult;
    return replaceResult;
  }

  // Without a previous row, still prefer in-place replace/collapse when same-identity peers
  // already exist — avoid inserting a duplicate active mapping.
  const model = targetEditor.getModel();
  if (model && !isUnsupportedInputBinding(nextBindingWithReceiver)) {
    const varName = resolveBindingReceiverVarName(model, nextBindingWithReceiver);
    const identityRanges = findIdentityDirectBindingRanges(model, varName, nextBindingWithReceiver);
    if (identityRanges.length > 0) {
      return replaceBindingInCode(targetEditor, nextBindingWithReceiver, nextBindingWithReceiver);
    }
  }

  if (nextBindingWithReceiver.device === DRIVEBASE) return insertDrivebaseBinding(targetEditor, nextBindingWithReceiver);
  return insertControllerBinding(targetEditor, nextBindingWithReceiver);
}

function bindingIdentityKey(binding: InputBinding): string {
  return `${String(binding.device || '')}|${String(binding.receiverVarName || '')}|${String(binding.button || '')}|${String(binding.kind || '')}`;
}

function bindingButtonKey(binding: InputBinding): string {
  return `${String(binding.device || '')}|${String(binding.receiverVarName || '')}|${String(binding.button || '')}`;
}

/**
 * Identity a source span is matched by: button + trigger + action, args ignored. Returns null
 * for kinds whose spans are not identity-matched (toggle collapses per button; unsupported
 * rows match on raw text).
 */
function bindingActionIdentityKey(binding: InputBinding): string | null {
  if (binding.kind === INPUT_BINDING_KIND.TOGGLE || isUnsupportedInputBinding(binding)) return null;
  const { action } = binding;
  return [bindingIdentityKey(binding), String(action.parentName || ''), String(action.mechanismName || ''), String(action.action?.name || '')].join(
    '|'
  );
}

/**
 * Replace collapses top-level same-identity peers into the rewritten call. Drop any other
 * next-list row that shares that action identity so Redux matches the single remaining call.
 */
function dropSameIdentityPeersCollapsedByReplace(
  previousBindings: ReadonlyArray<InputBinding>,
  nextBindings: ReadonlyArray<InputBinding>
): InputBinding[] {
  const previousSignatures = new Set(previousBindings.map(inputBindingSignature));
  const nextSignatures = new Set(nextBindings.map(inputBindingSignature));
  const toDelete = previousBindings.filter((binding) => !nextSignatures.has(inputBindingSignature(binding)));
  const toInsert = nextBindings.filter((binding) => !previousSignatures.has(inputBindingSignature(binding)));

  const sameKindPairing = pairSingleBindingReplacements([...toDelete], [...toInsert], bindingIdentityKey);
  const triggerChangePairing = pairSingleBindingReplacements(
    sameKindPairing.remainingDeletions,
    sameKindPairing.remainingInsertions,
    bindingButtonKey
  );
  const inPlacePairs = [...sameKindPairing.pairs, ...triggerChangePairing.pairs];

  const winningSignatures = new Set<string>();
  const collapsedIdentities = new Set<string>();
  for (const [previous, next] of inPlacePairs) {
    const identity = bindingActionIdentityKey(previous);
    if (identity === null) continue;
    const nextSignature = inputBindingSignature(next);
    const hasPeer = nextBindings.some(
      (binding) => inputBindingSignature(binding) !== nextSignature && bindingActionIdentityKey(binding) === identity
    );
    if (!hasPeer) continue;
    collapsedIdentities.add(identity);
    winningSignatures.add(nextSignature);
  }

  if (collapsedIdentities.size === 0) return [...nextBindings];

  return nextBindings.filter((binding) => {
    const identity = bindingActionIdentityKey(binding);
    if (identity === null || !collapsedIdentities.has(identity)) return true;
    return winningSignatures.has(inputBindingSignature(binding));
  });
}

function groupBindingsByKey(bindings: InputBinding[], keyFn: (binding: InputBinding) => string): Map<string, InputBinding[]> {
  const groups = new Map<string, InputBinding[]>();
  for (const binding of bindings) {
    const key = keyFn(binding);
    const group = groups.get(key);
    if (group) group.push(binding);
    else groups.set(key, [binding]);
  }
  return groups;
}

function pairSingleBindingReplacements(
  deletions: InputBinding[],
  insertions: InputBinding[],
  keyFn: (binding: InputBinding) => string
): {
  pairs: Array<[InputBinding, InputBinding]>;
  remainingDeletions: InputBinding[];
  remainingInsertions: InputBinding[];
} {
  const pairs: Array<[InputBinding, InputBinding]> = [];
  const deleteByKey = groupBindingsByKey(deletions, keyFn);
  const insertByKey = groupBindingsByKey(insertions, keyFn);
  const remainingDeletions: InputBinding[] = [];
  const remainingInsertions: InputBinding[] = [];

  for (const [key, deleteGroup] of deleteByKey) {
    const insertGroup = insertByKey.get(key) ?? [];
    if (deleteGroup.length === 1 && insertGroup.length === 1) {
      pairs.push([deleteGroup[0], insertGroup[0]]);
      insertByKey.delete(key);
    } else {
      remainingDeletions.push(...deleteGroup);
    }
  }

  for (const insertGroup of insertByKey.values()) {
    remainingInsertions.push(...insertGroup);
  }

  return { pairs, remainingDeletions, remainingInsertions };
}

function replaceBindingInCode(
  editor: MonacoEditor.IStandaloneCodeEditor,
  previousBinding: InputBinding,
  nextBinding: InputBinding
): CodeMutationResult {
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  const model = targetEditor.getModel();
  if (!model) return { ok: false, reason: 'No editor model available.' };
  const isDrivebase = previousBinding.device === DRIVEBASE;
  const varName = resolveBindingReceiverVarName(model, previousBinding);
  const newLine = buildFormattedDirectBindingLine(varName, nextBinding);
  if (!newLine) return { ok: false, reason: 'Could not format replacement binding line.' };

  // Tree-sitter owns span selection; fail closed when not ready (same contract as insert/delete).
  if (!isPythonTreeSitterReady()) {
    return treeSitterNotReadyResult('replace');
  }

  // Prefer a previous-identity match at the anchor. If the anchored call was hand-edited
  // away from that identity, fail closed — do not rewrite a same button+kind span in place.
  const semanticRanges = findSemanticDirectBindingRanges(model, varName, previousBinding);
  const semanticRange = selectAnchoredOrFirstBindingRange(semanticRanges, previousBinding);
  if (!semanticRange) {
    return { ok: true, changed: false };
  }

  const sourceSpan = readBindingSourceSpan(model, semanticRange);
  const preservedNewLine = withPreservedInlineComments(newLine, sourceSpan);
  const replacementLine = applyLeadingIndentFromLine(model.getLineContent(semanticRange.startLine), preservedNewLine);
  // Same button+trigger+action peers of the *previous* binding collapse into the rewritten primary.
  const previousIdentityPeers = findIdentityDirectBindingRanges(model, varName, previousBinding);
  const rangesForCollapse = [
    semanticRange,
    ...previousIdentityPeers.filter((range) => range.startLine !== semanticRange.startLine || range.endLine !== semanticRange.endLine),
  ];
  const duplicateRanges = peerBindingRanges(model, semanticRange, rangesForCollapse);
  if (bindingLineNeedsRelocation(model, semanticRange.startLine, nextBinding)) {
    const scopeRejection = rejectRelocationThatWouldChangeScope(model, semanticRange.startLine);
    if (scopeRejection) return scopeRejection;
    const device = isDrivebase ? 'drivebase' : 'controller';
    const deleteEdits: MonacoEditor.IIdentifiedSingleEditOperation[] = deletedLineNumbersFromRanges(duplicateRanges)
      .sort((left, right) => right - left)
      .map((lineNumber) => ({
        range: new Range(lineNumber, 1, lineNumber + 1, 1),
        text: '',
        forceMoveMarkers: true,
      }));
    return executeAtomicBindingCodeMutation(
      targetEditor,
      isDrivebase ? 'drivebase.mapping.relocate' : 'controller.mapping.relocate',
      (currentModel) => buildRelocatedBindingCode(currentModel, deleteEdits, varName, device, nextBinding, preservedNewLine, semanticRange.startLine)
    );
  }
  if (duplicateRanges.length === 1 && sourceSpan === replacementLine) return { ok: true, changed: false };
  const edits: MonacoEditor.IIdentifiedSingleEditOperation[] = [...duplicateRanges]
    .sort((left, right) => right.startLine - left.startLine)
    .map((range) => ({
      range: new Range(range.startLine, 1, range.endLine + 1, 1),
      text: range.startLine === semanticRange.startLine && range.endLine === semanticRange.endLine ? replacementLine + '\n' : '',
      forceMoveMarkers: true,
    }));
  return executeEdits(targetEditor, isDrivebase ? 'drivebase.mapping.replace' : 'controller.mapping.replace', edits);
}

/**
 * True when a top-level call already registers this binding's button + trigger + action
 * (args ignored). Nested spans are excluded: those are not the calls insert would collapse.
 */
function hasTopLevelSameIdentityBinding(editor: MonacoEditor.IStandaloneCodeEditor, binding: InputBinding): boolean {
  const model = editor.getModel();
  if (!model || isUnsupportedInputBinding(binding)) return false;
  const varName = resolveBindingReceiverVarName(model, binding);
  return findIdentityDirectBindingRanges(model, varName, binding).some((range) => isTopLevelBindingRange(model, range));
}

/**
 * Every mutation inside one save shifts the lines below it, so anchors captured before the save
 * started go stale mid-transaction. Re-resolve anchors for rows the save has not applied yet,
 * keeping the original anchor when nothing matches so genuinely stale rows still fail closed.
 */
function reanchorPendingBindingsInTransientSource(editor: MonacoEditor.IStandaloneCodeEditor, pending: ReadonlyArray<InputBinding>): InputBinding[] {
  const reanchored = reanchorInputBindingsToSource(editor, [...pending]);
  return pending.map((binding, index) => {
    const candidate = reanchored[index];
    return candidate && candidate.sourceLineNumber !== undefined ? candidate : binding;
  });
}

export function replaceButtonInputBindingsInCode(
  editor: MonacoEditor.IStandaloneCodeEditor,
  previousBindings: InputBinding[],
  nextBindings: InputBinding[]
): CodeMutationResult {
  const targetEditor = getMainProgramEditorProxy(editor) ?? editor;
  let failureResult: Extract<CodeMutationResult, { ok: false }> | null = null;

  const result = executeAtomicCodeMutation(targetEditor, 'button.mapping.save', (_model, currentCode) => {
    const { editor: transientEditor, getCode } = createTransientCodeEditor(currentCode);
    const canonicalPreviousBindings = canonicalizeButtonBindingsForSave(transientEditor, previousBindings, previousBindings);
    const canonicalNextBindings = collapseDuplicateToggleBindings(canonicalizeButtonBindingsForSave(transientEditor, previousBindings, nextBindings));
    const previousSignatures = new Set(canonicalPreviousBindings.map(inputBindingSignature));
    const nextDeduped = dedupeInputBindingsBySignature(canonicalNextBindings);
    const nextSignatures = new Set(nextDeduped.map(inputBindingSignature));

    const toDelete = canonicalPreviousBindings.filter((b) => !nextSignatures.has(inputBindingSignature(b)));
    const toInsert = nextDeduped.filter((b) => !previousSignatures.has(inputBindingSignature(b)));

    const sameKindPairing = pairSingleBindingReplacements(toDelete, toInsert, bindingIdentityKey);
    const triggerChangePairing = pairSingleBindingReplacements(
      sameKindPairing.remainingDeletions,
      sameKindPairing.remainingInsertions,
      bindingButtonKey
    );
    const inPlacePairs = [...sameKindPairing.pairs, ...triggerChangePairing.pairs];
    const remainingToDelete = triggerChangePairing.remainingDeletions;
    const remainingToInsert = triggerChangePairing.remainingInsertions;

    const reanchorPendingWork = (nextPairIndex: number, nextDeleteIndex: number) => {
      const pendingPairPrevious = inPlacePairs.slice(nextPairIndex).map(([prev]) => prev);
      const pendingDeletions = remainingToDelete.slice(nextDeleteIndex);
      if (pendingPairPrevious.length === 0 && pendingDeletions.length === 0) return;

      const reanchored = reanchorPendingBindingsInTransientSource(transientEditor, [...pendingPairPrevious, ...pendingDeletions]);
      pendingPairPrevious.forEach((_, offset) => {
        const pair = inPlacePairs[nextPairIndex + offset];
        const prev = reanchored[offset];
        if (!pair || !prev) return;
        inPlacePairs[nextPairIndex + offset] = [prev, pair[1]];
      });
      pendingDeletions.forEach((_, offset) => {
        const deletion = reanchored[pendingPairPrevious.length + offset];
        if (!deletion) return;
        remainingToDelete[nextDeleteIndex + offset] = deletion;
      });
    };

    for (let pairIndex = 0; pairIndex < inPlacePairs.length; pairIndex += 1) {
      const [prev, next] = inPlacePairs[pairIndex]!;
      const replaceResult = replaceBindingInCode(transientEditor, prev, next);
      if (!replaceResult.ok) {
        if (unsafeRelocationReasonShouldAbortButtonSave(replaceResult.reason)) {
          failureResult = replaceResult;
          return null;
        }
        remainingToDelete.push(prev);
        remainingToInsert.push(withBindingReceiverVarName(next, prev.receiverVarName));
        continue;
      }
      // Soft-no-op (e.g. stale anchor / identity mismatch): abort the whole save so callers
      // do not commit Redux or close the dialog while Main.py is unchanged.
      if (!replaceResult.changed) {
        failureResult = {
          ok: false,
          reason: STALE_MAPPING_IN_EDITOR_REASON.update,
        };
        return null;
      }
      reanchorPendingWork(pairIndex + 1, 0);
    }

    /**
     * Wipe top-level same button+kind peers, then queue every surviving UI row for that
     * button+kind. Used for on_toggle swaps where identity/args cross-match both spans —
     * not for stale-anchored soft-no-ops (those must leave hand-authored source alone).
     */
    const wipeButtonKindPeersPreservingSurvivors = (previousBinding: InputBinding, survivors: InputBinding[]): CodeMutationResult => {
      const wipeResult = wipeTopLevelButtonKindPeersFromCode(transientEditor, previousBinding);
      if (!wipeResult.ok) return wipeResult;
      for (const survivor of survivors) {
        if (remainingToInsert.some((binding) => inputBindingSignature(binding) === inputBindingSignature(survivor))) continue;
        remainingToInsert.push(withBindingReceiverVarName(survivor, previousBinding.receiverVarName));
      }
      return wipeResult;
    };

    for (let deleteIndex = 0; deleteIndex < remainingToDelete.length; deleteIndex += 1) {
      const previousBinding = remainingToDelete[deleteIndex]!;
      const sameKindSurvivors = nextDeduped.filter((binding) => bindingIdentityKey(binding) === bindingIdentityKey(previousBinding));

      // Swapped on_toggle siblings cross-match identity/args across both call spans. Deleting
      // the loser can wipe the winner too — wipe all top-level toggle peers, then re-insert.
      if (previousBinding.kind === INPUT_BINDING_KIND.TOGGLE && sameKindSurvivors.length > 0) {
        const wipeResult = wipeButtonKindPeersPreservingSurvivors(previousBinding, sameKindSurvivors);
        if (!wipeResult.ok) {
          failureResult = wipeResult;
          return null;
        }
        reanchorPendingWork(inPlacePairs.length, deleteIndex + 1);
        continue;
      }

      const deleteResult = deleteInputBindingFromCode(transientEditor, previousBinding);
      if (!deleteResult.ok) {
        failureResult = deleteResult;
        return null;
      }
      // Soft-no-op delete (stale anchor / unmatched span): fail the save rather than removing
      // the row from Redux while the call remains in Main.py.
      if (!deleteResult.changed) {
        failureResult = {
          ok: false,
          reason: STALE_MAPPING_IN_EDITOR_REASON.remove,
        };
        return null;
      }
      reanchorPendingWork(inPlacePairs.length, deleteIndex + 1);
    }

    for (const nextBinding of remainingToInsert) {
      // An unpaired row is a new mapping. When source already holds a top-level call with the
      // same button + trigger + action, inserting would collapse onto it — one call for two UI
      // rows. Fail the save instead of silently rewriting a mapping the dialog never edited.
      if (hasTopLevelSameIdentityBinding(transientEditor, nextBinding)) {
        failureResult = { ok: false, reason: DUPLICATE_MAPPING_IDENTITY_REASON };
        return null;
      }
      const insertResult = upsertInputBindingInCode(transientEditor, nextBinding);
      if (!insertResult.ok) {
        failureResult = insertResult;
        return null;
      }
    }

    return getCode();
  });

  return failureResult ?? result;
}
